/**
 * Section registry — defines all dashboard sections.
 * This is the frontend-side mirror of the backend SectionRegistry.
 * Used for routing, sidebar navigation, and section metadata.
 */
import {
  BarChart3,
  GitBranch,
  Smartphone,
  TrendingUp,
  Bell,
  Users,
  DollarSign,
  Activity,
  Settings,
  BrainCircuit,
  type LucideIcon,
} from 'lucide-react';

export interface Section {
  id: string;
  name: string;
  description: string;
  icon: LucideIcon;
  route: string;
  /** Whether the section is implemented and available */
  enabled: boolean;
  /** Color accent for the section (used in cards, badges) */
  accent: string;
}

export const sections: Section[] = [
  {
    id: 'product',
    name: 'Product',
    description: 'Explore events with time series charts and pivot tables',
    icon: BarChart3,
    route: '/product',
    enabled: true,
    accent: '#6366F1',
  },
  {
    id: 'marketing',
    name: 'Marketing',
    description: 'Analyze campaign performance and ad spend metrics',
    icon: TrendingUp,
    route: '/marketing',
    enabled: true,
    accent: '#EC4899',
  },
  {
    id: 'users',
    name: 'Users',
    description: 'Browse active users, view profiles, sessions, and acquisition details',
    icon: Users,
    route: '/users',
    enabled: true,
    accent: '#0EA5E9',
  },
  {
    id: 'purchases',
    name: 'Purchases',
    description: 'Track subscriptions, revenue, and subscriber lifecycle events',
    icon: DollarSign,
    route: '/purchases',
    enabled: true,
    accent: '#F59E0B',
  },
  {
    id: 'event_flow',
    name: 'Event Flow',
    description: 'Visualize user event sequences as interactive flow trees',
    icon: GitBranch,
    route: '/event-flow',
    enabled: true,
    accent: '#06B6D4',
  },
  {
    id: 'screen_flow',
    name: 'Screen Flow',
    description: 'Track screen-to-screen navigation patterns',
    icon: Smartphone,
    route: '/screen-flow',
    enabled: true,
    accent: '#10B981',
  },
  {
    id: 'push_notifications',
    name: 'Push Notifications',
    description: 'Send, translate, schedule, and analyze push notifications',
    icon: Bell,
    route: '/push-notifications',
    enabled: true,
    accent: '#8B5CF6',
  },
  {
    id: 'ai_chat',
    name: 'AI Chat',
    description: 'Ask AI about your data, run queries, and get insights',
    icon: BrainCircuit,
    route: '/ai-chat',
    enabled: true,
    accent: '#6366F1',
  },
  {
    id: 'activity',
    name: 'Activity',
    description: 'View all API calls, BigQuery queries, and estimated costs',
    icon: Activity,
    route: '/activity',
    enabled: true,
    accent: '#64748B',
  },
  {
    id: 'settings',
    name: 'Settings',
    description: 'Configure tables, credentials, and preferences',
    icon: Settings,
    route: '/settings',
    enabled: false,
    accent: '#64748B',
  },
];

export function getSection(id: string): Section | undefined {
  return sections.find((s) => s.id === id);
}

export function getSectionByRoute(route: string): Section | undefined {
  return sections.find((s) => s.route === route);
}

/** Only sections visible in the sidebar (not settings) */
export const navSections = sections.filter((s) => s.id !== 'settings');

/**
 * Every section id. Mirrors `SECTION_IDS` in `api/src/modules/auth/access.ts`,
 * which is the authority — this list only seeds the optimistic "all sections"
 * state used in dev mode and before `/api/auth/me` responds.
 */
export const ALL_SECTION_IDS = sections.map((s) => s.id);

/**
 * Find the section a pathname belongs to, so route guards can ask whether the
 * user may open it. Matches on the route prefix because sections own subpaths
 * like `/product/new` and `/product/reports/:id`.
 */
export function getSectionByPath(pathname: string): Section | undefined {
  return sections.find(
    (s) => pathname === s.route || pathname.startsWith(`${s.route}/`),
  );
}
